/**
 * ============================================================================
 * 教学内容 - Teaching Content
 * ============================================================================
 *
 * 【作用】控制“课程教学”页的页面标题、副标题、表头“学期/对象”、大纲链接文案，以及课程卡片列表。
 * 【如何修改】页面级文案改 title/subtitle/term/level/viewSyllabus；增删课程在 courses 数组中操作。
 *
 * 【课程对象参数说明】
 * - code: 课程代码字符串，如 "CHEM-PHYS 401"
 * - title / level / term: 均为 { en: "...", cn: "..." }
 * - abstract: { en, cn }，两句话摘要，用于列表/卡片展示
 * - desc: { en, cn }，详细描述，仅用于详情弹窗
 * - tags: 数组，每项为 { en: "...", cn: "..." }，用于卡片底部标签
 *
 * 【可选 - 大纲链接】不提供则卡片/详情中不显示大纲入口
 * - syllabusUrl: 可选。填写后显示“查看大纲”链接。可为：
 *   - 站内路径，如 "/teaching/chem101"（同站新页面，如 Jupyter Book 内置页）
 *   - 外部完整 URL，如 "https://xxx.github.io/yyy"（会新开标签打开）
 *
 * 【可选 - 学期标签】同一门课开过多个学期时，可标出以往学期
 * - semestersOffered: 可选。数组，每项 { en: "Fall 2024", cn: "2024 秋季" }，表示曾开课学期，用于展示“曾开课：…”标签
 *
 * 【可选 - 额外信息】除课程描述外可补充学分、课程目标等
 * - optionalSections: 可选。数组，每项 { label: { en: "Credits", cn: "学分" }, content: { en: "3", cn: "3" } }。
 *   常见示例：Credits/学分、Course Objectives/课程目标、Prerequisites/先修。按顺序在详情弹窗中展示。
 */

export const teachingContent = {
  title: { en: "Teaching", cn: "课程教学" },
  subtitle: { en: "Training the next generation of interdisciplinary scientists.", cn: "培养打破学科边界的下一代科学家。" },
  term: { en: "Term", cn: "学期" },
  level: { en: "Level", cn: "对象" },
  /** 大纲链接按钮/文字（当课程有 syllabusUrl 时显示） */
  viewSyllabus: { en: "View Syllabus", cn: "查看大纲" },
  /** 曾开课学期区块标题（当课程有 semestersOffered 时显示） */
  previouslyOffered: { en: "Previously offered", cn: "曾开课" },
  courses: [
    {
      code: "CHEM 101",
      title: { en: "Principles of Chemistry", cn: "化学原理" },
      level: { en: "Undergraduate", cn: "本科生" },
      term: { en: "Fall 2026", cn: "2026 秋季" },
      abstract: {
        en: "An undergraduate course of chemistry that builds from measurement and quantum foundations through thermodynamics and kinetics. Integrates computation, data, and AI with short frontier topics to connect fundamentals to modern research.",
        cn: "面向本科生的化学原理课程。以概念关系为主线，从测量与量子基础到热力学与动力学的化学原理课。结合计算、数据与 AI 及前沿专题，将基础与当代研究衔接。"
      },
      desc: {
        en: "This Principles of Chemistry course revolves around a core question: how do we really understand and quantify chemical systems? The course does not follow the traditional approach of covering topics one by one, but is structured according to how concepts relate to each other. First, we establish a common language of measurement, models, and data. Then, we go into the microscopic view of quantum mechanics and structure, and from there develop thermodynamics and equilibrium. The second half deals with kinetics and real reaction systems, and finally ties these basic principles to areas students will come across later, like materials, electrochemistry, organic, and biochemical systems. Contents you may have learned previously are not repeated in the old way; instead, they are quickly diagnosed and re-examined from a more unified, quantitative perspective. Computational tools, data analysis, and responsible use of AI run through the course, with short practical notebooks in every unit to help students build up a set of useful skills. Meanwhile, short frontier topics—like batteries, catalysis, or machine learning potentials—will let everyone see how these basics are used in today's chemical research. (Syllabus is under construction)",
        cn: "一门面向本科生的化学原理课程，学生将学习化学的基本概念和原理。这门《化学原理》课程会从“我们应该如何理解现代化学”这个核心问题出发，抛弃按照传统章节堆知识的教学方法。课程整体按照概念之间的依赖关系来展开：先建立测量、模型和数据的基础语言，再进入微观的量子与结构图像，随后过渡到热力学与平衡的宏观理解，接着讨论动力学和真实反应体系，最后把这些原理连接到材料、电化学、有机和生物等更贴近真实研究场景的内容。本课程用更统一、更深入浅出的视角重新理解学生学习过的普通化学内容，摈弃简单重复的教学。同时，计算工具、数据分析和基本的 AI 使用会贯穿全程，每一部分都有配套的小型计算/研究实践，让大家能够顺利衔接到后续专业课或进入实验室进行研究工作。在主线之外，课程还会穿插一些简短的前沿专题，比如电池、催化或机器学习势能面，帮助大家看到这些基础原理在当代化学中的真实应用。（大纲正在建设中）"
      },
      tags: [
        { en: "Chemistry", cn: "化学" },
        { en: "Core", cn: "核心课" }
      ],
      // 此处可填写大纲链接（站内路径如 "/teaching/chem101" 或外部 URL）
      // syllabusUrl: "/teaching/chem101",
      // 此处可填写曾开课学期，用于显示“曾开课：Fall 2024, Spring 2023”等
      // semestersOffered: [
      //   { en: "Fall 2024", cn: "2024 秋季" },
      //   { en: "Spring 2023", cn: "2023 春季" }
      // ],
      // 此处可填写除描述外的信息块（学分、课程目标等）
      // optionalSections: [
      //   { label: { en: "Credits", cn: "学分" }, content: { en: "3 units", cn: "3 学分" } },
      //   { label: { en: "Course Objectives", cn: "课程目标" }, content: { en: "…", cn: "…" } }
      // ]
    },
    {
      code: "OpenScience",
      title: { en: "Open Science Space", cn: "开放科学空间" },
      level: { en: "All Levels", cn: "所有级别" },
      term: { en: "All Terms", cn: "所有学期" },
      abstract: {
        en: "A multilingual open platform project for STEM that reorganizes textbook concepts with executable code and interactive content. Built on Jupyter Book and MyST, with plans for community-driven updates and LLM-assisted teaching.",
        cn: "面向理工科的多语言开放知识平台项目，用可运行代码与交互内容重组教材概念。基于 Jupyter Book 与 MyST 构建，计划支持社区共建与大语言模型辅助教学。"
      },
      desc: {
        en: "This project aims to build a multilingual open knowledge platform for STEM (with Chinese and English as the primary languages), reorganizing core concepts that are often scattered across textbooks into a more connected and accessible structure. Built on open-source tools such as MyST Markdown and Jupyter Book, the platform presents content in a textbook-style hierarchy enriched with executable code examples, interactive visualizations, and dense cross-linking to encourage exploration. Users can read online or download the source files to create their own notes. In the long term, we plan to integrate large language models to help synthesize emerging research and provide guided explanations, allowing the platform to evolve into a continuously updated, community-driven foundation for learning and teaching science.",
        cn: "这个项目希望搭建一个面向理工科的多语言开放知识平台（优先支持中文和英文），把分散在不同教材和资料里的核心内容，用更清晰、可互相连接的方式重新组织起来。平台基于 MyST Markdown 和 Jupyter Book 等开源工具构建，内容按教科书式结构呈现，同时配合可运行的代码示例、可视化演示和密集的知识链接，让学习过程更直观、更有探索感。用户不仅可以在线阅读，也可以下载源码自己修改做笔记。未来我们还计划引入大模型能力，帮助自动整理前沿进展和提供辅助解读，让这个平台逐步成为一个持续更新、社区共建的理工科知识基础设施。"
      },
      tags: [
        { en: "Open Science", cn: "开放科学" },
        { en: "Teaching", cn: "教学" }
      ],
      syllabusUrl: "https://openscienceteam.github.io/OpenScience/",
      // 此处可按需添加 syllabusUrl / semestersOffered / optionalSections
    }
  ]
};
